import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, GlobeIcon, ShieldCheckIcon, ZapIcon, ArrowRightIcon, UsersIcon, LockIcon, WifiIcon } from 'lucide-react';
export function WirelessCellphonesPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="bg-white" data-id="element-2422">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-2423">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-2424"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-2425"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2426">
          <div className="max-w-3xl" data-id="element-2427">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-2428">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2429">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2" data-id="element-2430"></span>
                Enterprise Mobility
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-2431">
                Business Wireless
                <br data-id="element-2432" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-2433">
                  & Cellphones
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-2434">
                Keep your team connected everywhere with enterprise-grade
                wireless plans and the latest mobile hardware. We provide
                managed mobile solutions that scale with your business.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-2435">
                View Business Plans
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2436" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-24 bg-white" data-id="element-2437">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2438">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-2439">
            {[{
            title: 'Nationwide Coverage',
            desc: 'Access the fastest 5G networks with reliable coverage across the country and international roaming options.',
            icon: GlobeIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            title: 'Unified Billing',
            desc: 'Consolidate all your mobile lines into a single, easy-to-manage monthly invoice with detailed usage reporting.',
            icon: ZapIcon,
            color: 'text-amber-500',
            bg: 'bg-amber-50'
          }, {
            title: 'Device Management',
            desc: 'Keep your company data secure with integrated mobile device management (MDM) and security protocols.',
            icon: ShieldCheckIcon,
            color: 'text-emerald-500',
            bg: 'bg-emerald-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="flex flex-col items-start" data-id="element-2440">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-2441">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-2442" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2443">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2444">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Hardware & Plans */}
      <section className="py-24 bg-slate-50" data-id="element-2445">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2446">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2447">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2448">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-2449">
                The Latest Devices for Your Team
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-2450">
                From the latest flagship smartphones to rugged devices for field
                work, we provide the hardware your team needs to stay
                productive.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2451">
                {[{
                title: 'Unlimited Data Plans',
                icon: WifiIcon
              }, {
                title: 'Shared Data Pools',
                icon: UsersIcon
              }, {
                title: 'Secure Hotspots',
                icon: LockIcon
              }, {
                title: 'Fleet Management',
                icon: SmartphoneIcon
              }].map((item, i) => <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-2452">
                    <item.icon className="w-5 h-5 text-blue-600" data-id="element-2453" />
                    <span className="font-bold text-slate-900 text-sm" data-id="element-2454">
                      {item.title}
                    </span>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-2455">
              <div className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-2456">
                <img src="https://images.unsplash.com/photo-1556656793-062ff987b50d?w=800&h=600&fit=crop" alt="Modern smartphones" className="w-full h-auto" data-id="element-2457" />
              </div>
              <div className="absolute -top-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100" data-id="element-2458">
                <div className="flex items-center gap-3" data-id="element-2459">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-2460">
                    <ZapIcon className="w-5 h-5 text-blue-600" data-id="element-2461" />
                  </div>
                  <div data-id="element-2462">
                    <div className="text-sm font-bold text-slate-900" data-id="element-2463">
                      5G Ready
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-2464">
                      Ultra-fast speeds
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-id="element-2465">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2466">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2467">
            Empower Your Mobile Workforce
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-2468">
            Get a custom quote for your business wireless needs and see how much
            you can save.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-2469">
            Request a Quote
          </button>
        </div>
      </section>
    </div>;
}